import React, { useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface DialerButtonProps {
  number: string;
  letters?: string;
  longPressValue?: string;
  onClick: (value: string) => void;
  className?: string;
}

const LONG_PRESS_DURATION = 500;

const DialerButton: React.FC<DialerButtonProps> = ({
  number,
  letters,
  longPressValue,
  onClick,
  className
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const isStarKey = number === '*';
  const isHashKey = number === '#';
  const isPlusLabel = letters === '+';
  const hasSubLabel = Boolean(letters);

  const handlePressStart = useCallback(() => {
    longPressTriggeredRef.current = false;

    if (longPressValue) {
      timerRef.current = setTimeout(() => {
        longPressTriggeredRef.current = true;
        onClick(longPressValue);
      }, LONG_PRESS_DURATION);
    }
  }, [longPressValue, onClick]);

  const handlePressEnd = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Only trigger normal click if long press wasn't triggered
    if (!longPressTriggeredRef.current) {
      onClick(number);
    }
  }, [number, onClick]);

  const handlePressCancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    longPressTriggeredRef.current = false;
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  return (
    <Button
      variant="ghost"
      className={cn(
        "relative w-[68px] h-[68px] short:w-[54px] short:h-[54px] flex flex-col items-center bg-muted hover:bg-muted/80 border border-border active:scale-95 transition-all duration-150 rounded-full select-none",
        hasSubLabel ? "justify-center gap-0" : "justify-center",
        className
      )}
      onPointerDown={handlePressStart}
      onPointerUp={handlePressEnd}
      onPointerCancel={handlePressCancel}
      onPointerLeave={handlePressCancel}
      onClick={handleClick}
    >
      <span className={cn(
        "font-light text-foreground text-2xl short:text-xl leading-tight",
        !hasSubLabel && "absolute inset-0 flex items-center justify-center",
        isStarKey && "text-[2.4rem] short:text-[2rem] font-normal leading-none translate-y-[8px] short:translate-y-[6px]",
        isHashKey && "text-[1.65rem] short:text-[1.2rem] font-light leading-none"
      )}>
        {number}
      </span>
      {hasSubLabel && (
        <span className={cn(
          "text-muted-foreground font-normal text-[10px] short:text-[9px] leading-none mt-1 h-3.5",
          isPlusLabel ? "text-[14px] short:text-[12px] tracking-normal" : "tracking-[0.2em]"
        )}>
          {letters}
        </span>
      )}
    </Button>
  );
};

export default DialerButton;
